// =============================================================================
// MeuDriver — NotificationPreferencesForm (Batch K2)
// Formulário de preferências de notificação com auto-save e debounce de 500ms.
// =============================================================================

import { useCallback, useEffect, useRef } from 'react'
import { Mail, Bell } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  type NotificationPreferences,
  type NotificationPreferencesUpdate,
} from '@/hooks/useNotificationPreferences'

// ---------------------------------------------------------------------------
// Configuração das opções por seção
// ---------------------------------------------------------------------------

interface PreferenceOption {
  key: keyof NotificationPreferences
  label: string
  description?: string
}

const EMAIL_OPTIONS: PreferenceOption[] = [
  {
    key: 'email_booking_confirmed',
    label: 'Aula confirmada',
    description: 'Receba um e-mail quando o pagamento da aula for aprovado.',
  },
  {
    key: 'email_booking_reminder',
    label: 'Lembrete de aula',
    description: 'E-mail de lembrete 24h antes da aula.',
  },
  {
    key: 'email_booking_cancelled',
    label: 'Aula cancelada',
    description: 'Notificação quando uma aula for cancelada.',
  },
  {
    key: 'email_new_review',
    label: 'Nova avaliação',
    description: 'E-mail quando você receber uma nova avaliação.',
  },
  {
    key: 'email_dispute_update',
    label: 'Atualização de disputa',
    description: 'Atualizações sobre disputas em aberto.',
  },
]

const PUSH_OPTIONS: PreferenceOption[] = [
  {
    key: 'push_booking_confirmed',
    label: 'Aula confirmada',
    description: 'Notificação push quando a aula for confirmada.',
  },
  {
    key: 'push_booking_reminder',
    label: 'Lembrete de aula',
    description: 'Push de lembrete próximo ao horário da aula.',
  },
  {
    key: 'push_new_message',
    label: 'Nova mensagem',
    description: 'Notificação quando receber uma nova mensagem.',
  },
  {
    key: 'push_dispute_update',
    label: 'Atualização de disputa',
    description: 'Push quando houver atualização em disputas.',
  },
]

// ---------------------------------------------------------------------------
// Toggle individual com debounce via ref
// ---------------------------------------------------------------------------

interface PreferenceToggleProps {
  option: PreferenceOption
  checked: boolean
  disabled: boolean
  onToggle: (key: keyof NotificationPreferences, value: boolean) => void
}

function PreferenceToggle({ option, checked, disabled, onToggle }: PreferenceToggleProps) {
  return (
    <div
      className="flex items-start justify-between gap-4 py-3 border-b border-border/50 last:border-0"
      data-testid={`pref-toggle-${option.key}`}
    >
      <div className="flex-1 min-w-0">
        <Label
          htmlFor={option.key}
          className="text-sm font-medium text-gray-800 cursor-pointer"
        >
          {option.label}
        </Label>
        {option.description && (
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            {option.description}
          </p>
        )}
      </div>
      <Switch
        id={option.key}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(val) => onToggle(option.key, val)}
        data-testid={`switch-${option.key}`}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Seção (E-mail ou Push)
// ---------------------------------------------------------------------------

interface SectionProps {
  icon: React.ReactNode
  title: string
  options: PreferenceOption[]
  prefs: NotificationPreferences | null
  disabled: boolean
  onToggle: (key: keyof NotificationPreferences, value: boolean) => void
}

function PreferenceSection({ icon, title, options, prefs, disabled, onToggle }: SectionProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
          {icon}
        </div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="rounded-xl border border-border/60 bg-white px-4">
        {options.map((opt) => (
          <PreferenceToggle
            key={opt.key}
            option={opt}
            checked={prefs ? Boolean(prefs[opt.key]) : true}
            disabled={disabled}
            onToggle={onToggle}
          />
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function NotificationPreferencesForm() {
  const { data: prefs, isLoading } = useNotificationPreferences()
  const { mutate: updatePrefs, isPending } = useUpdateNotificationPreferences()

  // Debounce via ref: acumula mudanças e envia após 500ms de inatividade
  const pendingUpdates = useRef<NotificationPreferencesUpdate>({})
  const debounceTimer  = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Limpa o timer ao desmontar
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [])

  const handleToggle = useCallback(
    (key: keyof NotificationPreferences, value: boolean) => {
      // Acumula a mudança
      pendingUpdates.current = { ...pendingUpdates.current, [key]: value }

      // Reinicia o debounce
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => {
        const updates = { ...pendingUpdates.current }
        pendingUpdates.current = {}
        updatePrefs(updates)
      }, 500)
    },
    [updatePrefs],
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="h-5 w-24" />
            <div className="rounded-xl border p-4 space-y-3">
              {[...Array(4)].map((_, j) => (
                <div key={j} className="flex items-center justify-between">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-5 w-10 rounded-full" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  const disabled = isPending

  return (
    <div className="space-y-6" data-testid="notification-preferences-form">
      <PreferenceSection
        icon={<Mail className="h-3.5 w-3.5 text-primary" />}
        title="E-mail"
        options={EMAIL_OPTIONS}
        prefs={prefs ?? null}
        disabled={disabled}
        onToggle={handleToggle}
      />
      <PreferenceSection
        icon={<Bell className="h-3.5 w-3.5 text-primary" />}
        title="Push"
        options={PUSH_OPTIONS}
        prefs={prefs ?? null}
        disabled={disabled}
        onToggle={handleToggle}
      />
    </div>
  )
}
